// @mui
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Box,
} from '@mui/material';
import { UnlinkConfirm, enrolledLine, removedLine } from './diplomaLinks';

// ----------------------------------------------------------------------

// The API refused a save that removes courses / tracks from a bootcamp with
// students (409 diploma_unlink_confirm): unlinking revokes the students'
// access to them and changes the batch team. Lists what would be removed and
// who has it; Confirm resends the same save with `confirmUnlink: true`.

type Props = {
  confirm: UnlinkConfirm | null;
  loading?: boolean;
  onCancel(): void;
  onConfirm(): void;
};

const FALLBACK =
  'Removing these closes them for students who have this bootcamp and for its batch team.';

export default function UnlinkConfirmDialog({
  confirm,
  loading = false,
  onCancel,
  onConfirm,
}: Props) {
  const enrolled = confirm ? enrolledLine(confirm.enrolled) : '';
  return (
    <Dialog open={!!confirm} onClose={loading ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Remove from this bootcamp?</DialogTitle>
      <DialogContent data-testid="unlink-confirm">
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          {confirm?.message || FALLBACK}
        </Typography>
        {enrolled && (
          <Typography
            variant="body2"
            sx={{ mb: 1.5, fontWeight: 600 }}
            data-testid="unlink-enrolled"
          >
            {enrolled}
          </Typography>
        )}
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          {(confirm?.removed || []).map((r) => (
            <Typography
              component="li"
              variant="body2"
              key={`${r.scope}:${r.id}`}
              data-testid="unlink-item"
            >
              {r.scope === 'track' ? 'Track: ' : 'Course: '}
              {removedLine(r)}
            </Typography>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading} color="inherit">
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          color="error"
          loading={loading}
          onClick={onConfirm}
          data-testid="unlink-confirm-button"
        >
          Remove and save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
